export type PostHogTransportRequest = Record<string, unknown>;

export type PostHogTransportQueue = {
  _clearFlushTimeout?: () => void;
  _isPolling?: boolean;
  _poller?: ReturnType<typeof setTimeout>;
  _queue?: unknown[];
};

export type PostHogTransportClient = {
  __request_queue?: unknown[];
  _requestQueue?: PostHogTransportQueue;
  _retryQueue?: PostHogTransportQueue;
  _send_request?: (options: PostHogTransportRequest) => void;
};

const transportGuardInstalled = Symbol("seoh-posthog-consent-transport-guard");
const transportEpochKey = "__seohPostHogConsentEpoch";

type GuardedPostHogTransportClient = PostHogTransportClient & {
  [transportGuardInstalled]?: true;
};

const isRequestOptions = (value: unknown): value is PostHogTransportRequest =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * PostHog's public opt-out only changes future capture decisions. Its private
 * retry queue can otherwise start a new request after a consent withdrawal.
 * This guard stays on the SDK instance (never on browser-wide transports).
 */
export const createPostHogConsentTransportController = (canCapture: () => boolean) => {
  let blocked = false;
  let epoch = 0;
  const requestEpochs = new WeakMap<object, number>();

  const canStart = () => !blocked && canCapture();

  const discardQueuedTransport = (client: PostHogTransportClient | null | undefined) => {
    if (!client) return;

    if (Array.isArray(client.__request_queue)) client.__request_queue.length = 0;

    const requestQueue = client._requestQueue;
    requestQueue?._clearFlushTimeout?.();
    if (Array.isArray(requestQueue?._queue)) requestQueue._queue.length = 0;

    const retryQueue = client._retryQueue;
    if (retryQueue?._poller !== undefined) clearTimeout(retryQueue._poller);
    if (retryQueue) {
      retryQueue._poller = undefined;
      retryQueue._isPolling = false;
    }
    if (Array.isArray(retryQueue?._queue)) retryQueue._queue.length = 0;
  };

  return {
    allow: () => {
      blocked = false;
    },
    install: (client: PostHogTransportClient) => {
      const guardedClient = client as GuardedPostHogTransportClient;
      if (guardedClient[transportGuardInstalled] || typeof client._send_request !== "function") return;

      const sendRequest = client._send_request.bind(client);
      client._send_request = (options) => {
        if (!canStart()) return;

        if (isRequestOptions(options)) {
          // RetryQueue shallow-clones options before it calls _send_request,
          // but its failure callback retains the original options object. The
          // data payload is shared by both, so retain the epoch there too.
          const epochTarget =
            options.data && typeof options.data === "object" ? options.data : options;
          const requestEpoch = requestEpochs.get(epochTarget) ?? options[transportEpochKey];
          if (typeof requestEpoch === "number" && requestEpoch !== epoch) return;
          requestEpochs.set(epochTarget, epoch);
          options[transportEpochKey] = epoch;
        }

        sendRequest(options);
      };
      guardedClient[transportGuardInstalled] = true;
    },
    withdraw: (client: PostHogTransportClient | null | undefined) => {
      // Increment before draining: an in-flight pre-withdrawal failure retains
      // its old epoch, so it remains blocked even if consent is later restored.
      epoch += 1;
      blocked = true;
      discardQueuedTransport(client);
    },
  };
};
